// Logout seguro (Fix 1 — hotfix pérdida de datos 31/07/2026). Se abre en vez
// de cerrar sesión directamente cuando hay registros sin subir: nunca deja
// que un logout normal destruya datos que no llegaron al servidor.
import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Progress } from "@/components/ui/progress";
import { AlertTriangle, CloudUpload, WifiOff, LogOut, Download, Check } from "lucide-react";
import { getPendingCount, onSyncState, syncNow } from "@/lib/sync";
import { downloadPendingBackup } from "@/lib/localBackup";
import { useAuth } from "@/lib/auth";
import { useOnline } from "@/lib/useOnline";
import { toast } from "sonner";

export function LogoutGuardModal({ open, onOpenChange, onLoggedOut }) {
  const { logout } = useAuth();
  const online = useOnline();
  const [pending, setPending] = useState(0);
  const [stage, setStage] = useState("review"); // review | draining | escape
  const [progress, setProgress] = useState({ pushed: 0, total: 0 });
  const [confirmText, setConfirmText] = useState("");
  const [backupDone, setBackupDone] = useState(false);

  useEffect(() => {
    if (!open) return;
    setStage("review");
    setConfirmText("");
    setBackupDone(false);
    getPendingCount().then(setPending);
  }, [open]);

  useEffect(() => {
    if (stage !== "draining") return;
    const off = onSyncState((s) => {
      if (s.phase === "draining" && s.progress) setProgress(s.progress);
    });
    return off;
  }, [stage]);

  const canDismiss = stage !== "draining";

  const handleSyncAndExit = async () => {
    setStage("draining");
    setProgress({ pushed: 0, total: pending });
    const result = await syncNow();
    const remaining = await getPendingCount();
    setPending(remaining);
    if (result?.error) {
      toast.error("No se pudo completar la sincronización. Reintentá.");
      setStage("review");
      return;
    }
    if (remaining > 0) {
      setStage("review");
      if (typeof navigator !== "undefined" && !navigator.onLine) {
        toast.error("Se perdió la conexión antes de terminar de subir todo.");
      } else {
        toast.error(`Quedaron ${remaining} registros con error — revisalos en Ajustes → Sincronización.`);
      }
      return;
    }
    try {
      await logout();
      onOpenChange(false);
      toast.success("Sesión cerrada — todo se sincronizó");
      onLoggedOut?.();
    } catch (e) {
      toast.error("No se pudo cerrar sesión, intentá de nuevo");
      setStage("review");
    }
  };

  const handleForceLogout = async () => {
    await logout({ force: true });
    onOpenChange(false);
    toast.success("Sesión cerrada");
    onLoggedOut?.();
  };

  const handleDownloadPending = async () => {
    try {
      const counts = await downloadPendingBackup();
      setBackupDone(true);
      toast.success(`Copia descargada: ${counts.animals || 0} animal(es) y ${counts.sync_queue || 0} operación(es) pendientes`);
    } catch (e) {
      toast.error("No se pudo generar la copia. Reintentá.");
    }
  };

  const label = pending === 1 ? "registro" : "registros";

  return (
    <Dialog
      open={open}
      onOpenChange={(next) => { if (!next && !canDismiss) return; onOpenChange(next); }}
    >
      <DialogContent
        data-testid="logout-guard-modal"
        className="sm:max-w-md"
        onInteractOutside={(e) => { if (!canDismiss) e.preventDefault(); }}
        onEscapeKeyDown={(e) => { if (!canDismiss) e.preventDefault(); }}
      >
        {stage === "review" && online && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CloudUpload size={18} className="text-[hsl(var(--brand-ochre))]" /> Tenés registros sin subir
              </DialogTitle>
              <DialogDescription>
                Tenés <span className="font-semibold text-foreground" data-testid="logout-pending-count">{pending}</span> {label} sin subir al servidor. Si cerrás sesión ahora, se podrían perder.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col sm:flex-col gap-2">
              <Button data-testid="logout-sync-and-exit-button" className="w-full h-11 bg-emerald-700 hover:bg-emerald-800" onClick={handleSyncAndExit}>
                <CloudUpload size={15} className="mr-1.5" /> Sincronizar y salir
              </Button>
              <Button variant="outline" data-testid="logout-cancel-button" className="w-full h-10" onClick={() => onOpenChange(false)}>Cancelar</Button>
              <button data-testid="logout-escape-link" onClick={() => setStage("escape")} className="text-[11px] text-muted-foreground hover:text-destructive underline underline-offset-2 mx-auto mt-1">
                ¿Necesitás salir igual? Opción avanzada
              </button>
            </DialogFooter>
          </>
        )}

        {stage === "review" && !online && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <WifiOff size={18} /> No podés cerrar sesión sin señal
              </DialogTitle>
              <DialogDescription>
                Tenés <span className="font-semibold text-foreground" data-testid="logout-pending-count">{pending}</span> {label} sin subir que se perderían. Conectate y sincronizá primero.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter className="flex-col sm:flex-col gap-2">
              <Button variant="outline" data-testid="logout-cancel-button" className="w-full h-10" onClick={() => onOpenChange(false)}>Entendido</Button>
              <button data-testid="logout-escape-link" onClick={() => setStage("escape")} className="text-[11px] text-muted-foreground hover:text-destructive underline underline-offset-2 mx-auto mt-1">
                Salir igual y perder los datos (soporte)
              </button>
            </DialogFooter>
          </>
        )}

        {stage === "draining" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                <CloudUpload size={18} className="text-primary animate-pulse" /> Subiendo registros…
              </DialogTitle>
              <DialogDescription>No cierres la app hasta que termine.</DialogDescription>
            </DialogHeader>
            <div className="space-y-2 py-2">
              <Progress value={progress.total ? Math.min(100, (progress.pushed / progress.total) * 100) : 0} data-testid="logout-drain-progress" />
              <div className="text-xs text-muted-foreground font-mono text-center" data-testid="logout-drain-count">
                {progress.pushed} / {progress.total || pending}
              </div>
            </div>
          </>
        )}

        {stage === "escape" && (
          <>
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2 text-destructive">
                <AlertTriangle size={18} /> Vas a perder datos sin subir
              </DialogTitle>
              <DialogDescription>
                Esta acción borra <span className="font-semibold text-foreground">{pending} {label}</span> que no llegaron al servidor. No se puede deshacer.
                <span className="block mt-2">
                  Primero descargá una copia de lo pendiente: es un archivo que se puede volver a cargar después
                  desde la importación de datos (modo "merge"), así nada se pierde.
                </span>
              </DialogDescription>
            </DialogHeader>
            <div className="space-y-3">
              <Button
                variant="outline"
                data-testid="logout-download-pending-button"
                onClick={handleDownloadPending}
                className="w-full h-11"
              >
                {backupDone ? <Check size={15} className="mr-1.5 text-emerald-600" /> : <Download size={15} className="mr-1.5" />}
                {backupDone ? "Copia descargada — descargar de nuevo" : "Descargar copia de lo pendiente"}
              </Button>
              {backupDone && (
                <p className="text-[11px] text-muted-foreground" data-testid="logout-escape-hint">
                  Guardá ese archivo antes de continuar. Para recuperarlo: Ajustes → Exportar / Importar datos, modo "merge".
                  Ahora escribí <span className="font-mono font-semibold text-foreground">BORRAR</span> para confirmar.
                </p>
              )}
              <Input
                data-testid="logout-escape-confirm-input"
                value={confirmText}
                onChange={(e) => setConfirmText(e.target.value)}
                placeholder="BORRAR"
                className="h-11 font-mono"
                autoComplete="off"
                disabled={!backupDone}
              />
            </div>
            <DialogFooter className="flex-col sm:flex-col gap-2">
              <Button
                variant="destructive"
                disabled={!backupDone || confirmText.trim().toUpperCase() !== "BORRAR"}
                onClick={handleForceLogout}
                data-testid="logout-escape-confirm-button"
                className="w-full h-11"
              >
                <LogOut size={15} className="mr-1.5" /> Salir y perder los datos
              </Button>
              <Button variant="outline" className="w-full h-10" onClick={() => setStage("review")} data-testid="logout-escape-cancel-button">Volver</Button>
            </DialogFooter>
          </>
        )}
      </DialogContent>
    </Dialog>
  );
}
